import { createSlice } from '@reduxjs/toolkit';
import type { TopicComment } from '../../@types/topic';

interface TopicState {
  comments: TopicComment[];
  loading: boolean;
  submitting: boolean;
  error: boolean;
}

interface TopicCommentsState {
  byTopic: Record<string, TopicState>;
  unreadGuids: string[];
}

const initialState: TopicCommentsState = {
  byTopic: {},
  unreadGuids: []
};

const defaultTopicState = (): TopicState => ({
  comments: [],
  loading: false,
  submitting: false,
  error: false
});

export const slice = createSlice({
  name: 'topicComments',
  initialState,
  reducers: {
    startLoading(state, action: { payload: string }) {
      const s = state.byTopic[action.payload] ?? defaultTopicState();
      s.loading = true;
      s.error = false;
      state.byTopic[action.payload] = s;
    },
    startSubmitting(state, action: { payload: string }) {
      const s = state.byTopic[action.payload] ?? defaultTopicState();
      s.submitting = true;
      s.error = false;
      state.byTopic[action.payload] = s;
    },
    getCommentsSuccess(
      state,
      action: { payload: { topicGuid: string; comments: TopicComment[] } }
    ) {
      const { topicGuid, comments } = action.payload;
      const s = state.byTopic[topicGuid] ?? defaultTopicState();
      s.loading = false;
      s.comments = comments;
      state.byTopic[topicGuid] = s;
    },
    addCommentSuccess(state, action: { payload: { topicGuid: string; comment: TopicComment } }) {
      const { topicGuid, comment } = action.payload;
      const s = state.byTopic[topicGuid] ?? defaultTopicState();
      s.submitting = false;
      if (!s.comments.some((c) => c.guid === comment.guid)) {
        s.comments = [...s.comments, comment];
      }
      state.byTopic[topicGuid] = s;
    },
    mergeComments(state, action: { payload: { topicGuid: string; comments: TopicComment[] } }) {
      const { topicGuid, comments } = action.payload;
      const s = state.byTopic[topicGuid] ?? defaultTopicState();
      const serverGuids = new Set(comments.map((c) => c.guid));
      const existingGuids = new Set(s.comments.map((c) => c.guid));
      const hasAdditions = comments.some((c) => !existingGuids.has(c.guid));
      const hasDeletions = s.comments.some((c) => !serverGuids.has(c.guid));
      if (hasAdditions || hasDeletions) {
        s.comments = comments;
        state.byTopic[topicGuid] = s;
      }
    },
    removeCommentSuccess(state, action: { payload: { topicGuid: string; commentGuid: string } }) {
      const { topicGuid, commentGuid } = action.payload;
      const s = state.byTopic[topicGuid];
      if (s) {
        s.comments = s.comments.filter((c) => c.guid !== commentGuid);
      }
    },
    setUnreadGuids(state, action: { payload: string[] }) {
      state.unreadGuids = action.payload;
    },
    markTopicRead(state, action: { payload: string }) {
      state.unreadGuids = state.unreadGuids.filter((g) => g !== action.payload);
    },
    hasError(state, action: { payload: string }) {
      const s = state.byTopic[action.payload] ?? defaultTopicState();
      s.loading = false;
      s.submitting = false;
      s.error = true;
      state.byTopic[action.payload] = s;
    }
  }
});

export default slice.reducer;
